import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import useUrlTableState from '../../../hooks/useUrlTableState';
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
  Search,
  ChevronLeft,
  ChevronRight,
  MapPin,
  List,
  Eye,
  CheckCircle,
  AlertCircle,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Upload
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { formatDate } from "../../../utils/stringUtils";
import { holidayTypeService } from "../../../api/crm/config/configService";
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from "../../../utils/formValidation";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";

const HolidayCalendar = ({ onBack, userRole, initialMode = "list", id, onClose }) => {
  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterType: '',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: holidayFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateHolidayFilter,
    updatePage: updateHolidayPage,
    updateLimit: updateHolidayLimit,
    resetFilters: resetHolidayFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterType = holidayFilters.filterType || 'all';
  const setFilterType = useCallback((type) => {
    updateHolidayFilter({ filterType: type === 'all' ? '' : type });
  }, [updateHolidayFilter]);

  const filterStartDate = holidayFilters.startDate || '';
  const filterEndDate = holidayFilters.endDate || '';

  const setDateRange = useCallback((range) => {
    updateHolidayFilter({ startDate: range.startDate || '', endDate: range.endDate || '' });
  }, [updateHolidayFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateHolidayFilter({ startDate });
  }, [updateHolidayFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateHolidayFilter({ endDate });
  }, [updateHolidayFilter]);

  const currentPage = holidayFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateHolidayPage(page);
  }, [updateHolidayPage]);

  const itemsPerPage = holidayFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateHolidayLimit(limit);
  }, [updateHolidayLimit]);

  const [holidays, setHolidays] = useState([]);
  const [filteredHolidays, setFilteredHolidays] = useState([]);
  const [availableHolidayTypes, setAvailableHolidayTypes] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  const resolveHolidayTypeId = (holiday) => {
    const directId = holiday?.holidayType?._id || holiday?.holidayType;
    if (directId) return directId;
    const typeName = (holiday?.type || "").trim().toLowerCase();
    if (!typeName) return "";
    const match = availableHolidayTypes.find((item) => (item?.name || "").trim().toLowerCase() === typeName);
    return match?._id || "";
  };

  // Handle deep-linking to specific holiday
  useEffect(() => {
    if (!id) return;

    const fetchHolidayById = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const url = `${process.env.REACT_APP_API_BASE_URL}/holidays/list/${id}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        const result = await response.json();
        if ((result.success || result.responseCode === 200) && result.data) {
          if (initialMode === "view") {
            setSelectedHoliday(result.data);
            setShowDetails(true);
          } else if (initialMode === "edit") {
            const holiday = result.data;
            setFormData({
              name: holiday.name || "",
              date: holiday.date || "",
              endDate: holiday.endDate || "",
              type: holiday.type || "",
              holidayType: resolveHolidayTypeId(holiday),
              description: holiday.description || "",
              location: holiday.location || "All",
              isOptional: holiday.isOptional || false,
              isRecurring: holiday.isRecurring || false,
              recurringType: holiday.recurringType || "none",
              applicableRoles: holiday.applicableRoles || "All",
              applicableLocations: holiday.applicableLocations || [],
              isActive: holiday.isActive ?? true,
            });
            setEditingHoliday(holiday);
            setShowHolidayForm(true);
          }
        }
      } catch (error) {
      }
    };

    fetchHolidayById();
  }, [id, initialMode]);

  const [showHolidayForm, setShowHolidayForm] = useState(initialMode === "create");
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [showDetails, setShowDetails] = useState(initialMode === "view");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(initialMode === "calendar" ? "calendar" : "list"); // calendar, list
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    endDate: "",
    type: "",
    holidayType: "", // Store ID here
    description: "",
    location: "All Locations",
    isOptional: false,
    isRecurring: false,
    recurringType: "yearly",
    applicableRoles: ["All Employees"],
    applicableLocations: [],
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch holiday types
  useEffect(() => {
    const fetchHolidayTypes = async () => {
      try {
        const response = await holidayTypeService.getDropdown();
        if (response.success && response.data) {
          setAvailableHolidayTypes(response.data);
        }
      } catch (error) {
      }
    };
    fetchHolidayTypes();
  }, []);

  // Reset selection when filters, search, pagination or viewMode changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [searchTerm, filterType, currentPage, viewMode, currentDate, filterStartDate, filterEndDate]);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Build base export filters
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      filterType: filterType !== 'all' ? filterType : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterType, filterStartDate, filterEndDate]);

  // Get current page holiday IDs
  const currentPageHolidayIds = useMemo(
    () =>
      holidays
        .map((h) => {
          const raw = h?._id ?? h?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [holidays]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageHolidayIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'holidays',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortField || 'date',
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `holidays-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortField,
      sortOrder,
      currentPageHolidayIds,
    ]
  );

  // Handle export for selected holidays with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected holidays to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageHolidayIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      holidays.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageHolidayIds,
    ]
  );

  // Export click handler
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Load holidays with pagination - useUrlTableState handles debouncing
  useEffect(() => {
    loadHolidays();
  }, [searchTerm, filterType, currentPage, itemsPerPage, filterStartDate, filterEndDate]);

  // For calendar view, still use filtered holidays
  useEffect(() => {
    if (viewMode === "calendar") {
      filterHolidays();
    }
  }, [holidays, viewMode, searchTerm, filterType, filterStartDate, filterEndDate]);

  const loadHolidays = async () => {
    setLoading(true);
    setIsSearching(true);
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        loadMockData();
        return;
      }

      // Build query parameters
      const queryParams = new URLSearchParams();

      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      if (filterType !== 'all') {
        queryParams.append('filterType', filterType);
      }

      // Add pagination
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());

      if (filterStartDate) {
        queryParams.append('startDate', filterStartDate);
      }
      if (filterEndDate) {
        queryParams.append('endDate', filterEndDate);
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/holidays/list?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.status === "success") {
        setHolidays(result.data);

        // Update pagination state from API response
        if (result.pagination) {
          setPagination({
            currentPage: result.pagination.currentPage || currentPage,
            totalPages: result.pagination.totalPages || 1,
            totalItems: result.pagination.totalHolidays || result.pagination.totalItems || 0,
            itemsPerPage: result.pagination.limit || itemsPerPage,
            hasNextPage: result.pagination.hasNextPage ?? false,
            hasPrevPage: result.pagination.hasPrevPage ?? false,
            limit: result.pagination.limit || itemsPerPage
          });
        }
      } else {
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: itemsPerPage,
          hasNextPage: false,
          hasPrevPage: false,
          limit: itemsPerPage
        });
      }
    } catch (error) {
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: itemsPerPage,
        hasNextPage: false,
        hasPrevPage: false,
        limit: itemsPerPage
      });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const loadMockData = () => {
    // Mock holidays data
    const mockHolidays = [
      {
        id: "HOL001",
        name: "New Year's Day",
        date: "2024-01-01",
        endDate: "2024-01-01",
        type: "National Holiday",
        description: "Celebration of the new year",
        location: "All Locations",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL002",
        name: "Republic Day",
        date: "2024-01-26",
        endDate: "2024-01-26",
        type: "National Holiday",
        description: "Indian Republic Day celebration",
        location: "India",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL003",
        name: "Holi",
        date: "2024-03-25",
        endDate: "2024-03-25",
        type: "Regional Holiday",
        description: "Festival of colors",
        location: "North India",
        isOptional: true,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL004",
        name: "Good Friday",
        date: "2024-03-29",
        endDate: "2024-03-29",
        type: "Regional Holiday",
        description: "Christian religious holiday",
        location: "All Locations",
        isOptional: true,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL005",
        name: "Independence Day",
        date: "2024-08-15",
        endDate: "2024-08-15",
        type: "National Holiday",
        description: "Indian Independence Day",
        location: "India",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL006",
        name: "Gandhi Jayanti",
        date: "2024-10-02",
        endDate: "2024-10-02",
        type: "National Holiday",
        description: "Mahatma Gandhi's birthday",
        location: "India",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL007",
        name: "Diwali",
        date: "2024-11-01",
        endDate: "2024-11-01",
        type: "Regional Holiday",
        description: "Festival of lights",
        location: "All Locations",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL008",
        name: "Christmas Day",
        date: "2024-12-25",
        endDate: "2024-12-25",
        type: "National Holiday",
        description: "Christian religious holiday",
        location: "All Locations",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL009",
        name: "Company Foundation Day",
        date: "2024-06-15",
        endDate: "2024-06-15",
        type: "Company Holiday",
        description: "Annual company celebration",
        location: "All Locations",
        isOptional: false,
        isRecurring: true,
        recurringType: "yearly",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
      {
        id: "HOL010",
        name: "Team Outing",
        date: "2024-09-20",
        endDate: "2024-09-21",
        type: "Company Event",
        description: "Annual team building event",
        location: "Goa",
        isOptional: true,
        isRecurring: false,
        recurringType: "none",
        applicableRoles: ["All Employees"],
        isActive: true,
        createdDate: "2024-01-01",
        lastModified: "2024-01-01",
      },
    ];
    setHolidays(mockHolidays);
  };



  const locations = [
    "India",
    "North India",
    "South India",
    "East India",
    "West India",
    "Central India",
    "Northeast India",
  ];

  const roles = [
    "All Employees",
    "Male",
    "Female",
  ];

  const recurringTypes = [
    { value: "none", label: "No Recurrence" },
    { value: "yearly", label: "Yearly" },
    { value: "monthly", label: "Monthly" },
    { value: "weekly", label: "Weekly" },
  ];

  // Date utilities - ensure local, timezone-agnostic handling of date strings
  const parseDateStringLocal = (dateStr) => {
    if (!dateStr) return null;
    // Construct local date without timezone shifts
    const [y, m, d] = dateStr.split("-").map((v) => parseInt(v, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  const toYMD = (date) => {
    if (!(date instanceof Date)) return null;
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  };

  const compareYMD = (a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    if (a.month !== b.month) return a.month - b.month;
    return a.day - b.day;
  };

  const formatDateForDisplay = (dateStr) => {
    const d = parseDateStringLocal(dateStr);
    if (!d) return "N/A";
    return d.toLocaleDateString();
  };

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Validation function for description
  const validateDescription = (description) => {
    if (!description || description.trim().length < 10) {
      return "Description must be at least 10 characters";
    }
    if (description.length > 500) {
      return "Description must be no more than 500 characters";
    }
    if (!VALID_TEXT_REGEX.test(description)) {
      return "Description contains invalid characters";
    }
    return null;
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get sorted holidays
  const getSortedHolidays = (holidays) => {
    if (!sortField || !holidays || holidays.length === 0) {
      return holidays;
    }

    const sorted = [...holidays].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;

        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;

        case 'type':
          aValue = (a.type || '').toLowerCase();
          bValue = (b.type || '').toLowerCase();
          break;

        case 'status':
          aValue = (a.isActive ? 'active' : 'inactive');
          bValue = (b.isActive ? 'active' : 'inactive');
          break;

        case 'createdDate':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;

        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };


  const filterHolidays = () => {
    let filtered = holidays;

    if (searchTerm) {
      filtered = filtered.filter(
        (holiday) =>
          holiday.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          holiday.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          holiday.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== "all") {
      filtered = filtered.filter((holiday) => {
        const holidayTypeId = (holiday.holidayType?._id || holiday.holidayType);
        const holidayTypeName = (holiday.type || "").toLowerCase();

        // Match by ID if it's a valid ID, otherwise match by name
        if (holidayTypeId && holidayTypeId === filterType) return true;

        // Fallback for legacy data or when matching by name
        const filterTypeObj = availableHolidayTypes.find(t => t._id === filterType);
        const filterName = filterTypeObj ? filterTypeObj.name.toLowerCase() : filterType.toLowerCase();

        return holidayTypeName === filterName;
      });
    }

    setFilteredHolidays(filtered);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Text fields: allow alphanumeric, spaces, and approved symbols
    if (['name', 'description'].includes(name)) {
      processedValue = value.replace(INVALID_CHARS_REGEX, '');
      // Enforce maxLength
      if (name === 'name') {
        if (processedValue.length > 100) processedValue = processedValue.slice(0, 100);
      } else if (name === 'description') {
        if (processedValue.length > 500) processedValue = processedValue.slice(0, 500);
      }
    }

    const newValue = type === "checkbox" ? checked : (processedValue || value);

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      // Auto-set endDate to startDate if startDate changes and endDate is empty or before startDate
      if (name === "date" && (!updated.endDate || updated.endDate < newValue)) {
        updated.endDate = newValue;
      }
      return updated;
    });

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value.trim()) error = "Holiday Name is required";
        break;
      case "holidayType":
        if (!value) error = "Holiday Type is required";
        break;
      case "date":
        if (!value) error = "Start Date is required";
        break;
      case "description":
        if (!value.trim()) {
          error = "Description is required";
        } else if (value.trim().length < 10) {
          error = "Description must be at least 10 characters";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Calculate duration helper function
  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return 0;
    const start = parseDateStringLocal(startDate);
    const end = parseDateStringLocal(endDate || startDate);
    if (!start || !end) return 0;
    // Difference in days, inclusive
    const msPerDay = 24 * 60 * 60 * 1000;
    const startYMD = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endYMD = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    const diffDays = Math.floor((endYMD - startYMD) / msPerDay);
    return diffDays + 1;
  };

  // Helper function to check if a holiday should appear on a specific date
  const shouldHolidayAppearOnDate = (holiday, date) => {
    // If holiday is not active, don't show it
    if (!holiday.isActive) {
      return false;
    }

    // Prepare local dates without timezone shifts
    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    const startDate = parseDateStringLocal(holiday.date);
    const endDate = parseDateStringLocal(holiday.endDate || holiday.date);
    if (!startDate || !endDate) return false;

    if (!holiday.isRecurring || holiday.recurringType === 'none') {
      // Non-recurring: simple inclusive range on Y/M/D
      const c = toYMD(checkDate);
      const s = toYMD(startDate);
      const e = toYMD(endDate);
      return compareYMD(s, c) <= 0 && compareYMD(c, e) <= 0;
    }

    const cMonth = checkDate.getMonth() + 1;
    const cDay = checkDate.getDate();
    const sMonth = startDate.getMonth() + 1;
    const sDay = startDate.getDate();
    const eMonth = endDate.getMonth() + 1;
    const eDay = endDate.getDate();

    if (holiday.recurringType === 'yearly') {
      // If single day
      if (sMonth === eMonth && sDay === eDay) {
        return cMonth === sMonth && cDay === sDay;
      }
      // Range within same month
      if (sMonth === eMonth) {
        return cMonth === sMonth && cDay >= sDay && cDay <= eDay;
      }
      // Range across months (e.g., Jan 30 - Feb 2)
      return (
        (cMonth === sMonth && cDay >= sDay) ||
        (cMonth === eMonth && cDay <= eDay) ||
        (cMonth > sMonth && cMonth < eMonth)
      );
    }

    if (holiday.recurringType === 'monthly') {
      // Repeats by day of month
      if (sDay === eDay) {
        return cDay === sDay;
      }
      return cDay >= sDay && cDay <= eDay;
    }

    if (holiday.recurringType === 'weekly') {
      return checkDate.getDay() === startDate.getDay();
    }

    return false;
  };

  const handleRoleChange = (role) => {
    setFormData((prev) => ({
      ...prev,
      applicableRoles: prev.applicableRoles.includes(role)
        ? prev.applicableRoles.filter((r) => r !== role)
        : [...prev.applicableRoles, role],
    }));
  };

  const handleSubmitHoliday = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        toast.error(
          "Please login to create holidays. You need to be logged in to create holidays."
        );
        return;
      }

      const holidayData = {
        name: formData.name,
        date: formData.date,
        endDate: formData.endDate || formData.date,
        type: formData.type,
        holidayType: formData.holidayType,
        description: formData.description,
        location: formData.location,
        isOptional: formData.isOptional,
        isRecurring: formData.isRecurring,
        recurringType: formData.isRecurring ? formData.recurringType : "none",
        applicableRoles: formData.applicableRoles,
        applicableLocations: formData.applicableLocations || [],
        isActive: formData.isActive,
      };

      // Validate required fields
      // Check each required field individually
      const missingFields = [];
      const errors = {};

      if (!holidayData.name || holidayData.name.trim() === "")
        missingFields.push("Holiday Name");
      if (!holidayData.date || holidayData.date.trim() === "")
        missingFields.push("Start Date");
      if (!holidayData.type || holidayData.type.trim() === "")
        missingFields.push("Holiday Type");
      if (!holidayData.description || holidayData.description.trim() === "")
        missingFields.push("Description");

      // Validate description length
      const descriptionError = validateDescription(holidayData.description);
      if (descriptionError) {
        errors.description = descriptionError;
      }

      if (missingFields.length > 0) {
        toast.error(
          "Please fill in all required fields:\n• " + missingFields.join("\n• ")
        );
        return;
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      // Set default values for location and applicableRoles
      if (!holidayData.location || holidayData.location.trim() === "") {
        holidayData.location = "All Locations";
      }
      if (
        !holidayData.applicableRoles ||
        holidayData.applicableRoles.length === 0
      ) {
        holidayData.applicableRoles = ["All Employees"];
      }

      const url = editingHoliday
        ? `${process.env.REACT_APP_API_BASE_URL}/holidays/list/${editingHoliday.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/holidays/list`;

      const method = editingHoliday ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(holidayData),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorResult.responseMessage ||
          errorResult.message ||
          "Unknown error"
          }`
        );
      }

      const result = await response.json();

      if (result.status === "success" || result.responseCode === 201) {
        toast.success("Holiday created successfully!");
        await loadHolidays();
        resetForm();
      } else {
        toast.error(
          "Failed to submit holiday: " +
          (result.responseMessage || result.message || "Unknown error")
        );
      }
    } catch (error) {
      if (error.message.includes("duplicate key")) {
        toast.error(
          "A holiday with this name and date already exists. Please choose a different name or date."
        );
      } else if (error.message.includes("validation")) {
        toast.error("Please check all required fields are filled correctly.");
      } else {
        toast.error("Failed to submit holiday. Please try again.");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      date: "",
      endDate: "",
      type: "",
      description: "",
      location: "All Locations",
      isOptional: false,
      isRecurring: false,
      recurringType: "yearly",
      applicableRoles: ["All Employees"],
      applicableLocations: [],
      isActive: true,
    });
    setFormErrors({});
    setShowHolidayForm(false);
    setEditingHoliday(null);
    if (onClose) onClose();
  };

  const handleEditHoliday = (holiday) => {
    const formData = {
      name: holiday.name,
      date: holiday.date,
      endDate: holiday.endDate,
      type: holiday.type,
      description: holiday.description,
      location: holiday.location,
      isOptional: holiday.isOptional,
      isRecurring: holiday.isRecurring,
      recurringType: holiday.recurringType,
      applicableRoles: holiday.applicableRoles,
      applicableLocations: holiday.applicableLocations || [],
      isActive: holiday.isActive,
      holidayType: resolveHolidayTypeId(holiday),
    };
    setFormData(formData);
    setFormErrors({});
    setEditingHoliday(holiday);
    setShowHolidayForm(true);
  };

  useEffect(() => {
    if (!showHolidayForm) return;
    if (formData.holidayType) return;
    if (!formData.type) return;
    if (!availableHolidayTypes.length) return;

    const match = availableHolidayTypes.find(
      (item) => (item?.name || "").trim().toLowerCase() === formData.type.trim().toLowerCase()
    );
    if (!match?._id) return;

    setFormData((prev) => ({
      ...prev,
      holidayType: match._id,
    }));
  }, [availableHolidayTypes, formData.holidayType, formData.type, showHolidayForm]);

  const handleDeleteHoliday = (holidayId, event) => {
    // Prevent any default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Find the holiday to get details
    const holiday = holidays.find((h) => h.id === holidayId || h._id === holidayId);
    if (!holiday) {
      toast.error("Holiday not found!");
      return;
    }

    // Set the holiday to delete and show the modal
    setHolidayToDelete(holiday);
    setShowDeleteModal(true);
  };

  const confirmDeleteHoliday = async () => {
    if (!holidayToDelete) return;

    try {
      const token = localStorage.getItem("auth_token");
      const actualId = holidayToDelete.id || holidayToDelete._id;
      const holidayName = holidayToDelete.name || "Holiday";

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/list/${actualId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      const data = await response.json();

      if (response.ok && (data.success || data.status === "success")) {
        toast.success(`Holiday "${holidayName}" has been deleted successfully!`);
        // Reload holidays from server to ensure pagination is correct
        await loadHolidays();
      } else {
        // Show error message if API call failed
        const errorMessage = data.message || data.error || `Failed to delete holiday (Status: ${response.status})`;
        toast.error(`Failed to delete holiday: ${errorMessage}`);
      }
    } catch (error) {
      toast.error(`Failed to delete holiday: ${error.message || "Network error. Please try again."}`);
    } finally {
      // Close modal and reset state
      setShowDeleteModal(false);
      setHolidayToDelete(null);
    }
  };

  const cancelDeleteHoliday = () => {
    setShowDeleteModal(false);
    setShowBulkDeleteModal(false);
    setHolidayToDelete(null);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const ids = Array.from(selectedRows);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/list/bulk-delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ ids }),
        }
      );

      const data = await response.json();

      if (response.ok && (data.success || data.status === "success")) {
        toast.success(`${data.data?.deletedCount || ids.length} holidays have been deleted successfully!`);
        setSelectedRows(new Set());
        await loadHolidays();
      } else {
        const errorMessage = data.message || data.error || `Failed to delete holidays (Status: ${response.status})`;
        toast.error(`Failed to bulk delete: ${errorMessage}`);
      }
    } catch (error) {
      toast.error(`Failed to bulk delete: ${error.message || "Network error. Please try again."}`);
    } finally {
      setShowBulkDeleteModal(false);
    }
  };


  const handleViewDetails = (holiday) => {
    setSelectedHoliday(holiday);
    setShowDetails(true);
  };

  const toggleHolidayStatus = async (holidayId) => {
    // Find the holiday to get current status
    const holiday = holidays.find((h) => h.id === holidayId);
    if (!holiday) {
      toast.error("Holiday not found!");
      return;
    }

    try {
      const token = localStorage.getItem("auth_token");
      if (token) {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/holidays/list/${holidayId}/toggle`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
        } else {
        }
      }

      // ONLY NOW update the holiday status after user confirmed
      setHolidays((prevHolidays) => {
        const updated = prevHolidays.map((h) => {
          if (h.id === holidayId) {
            return {
              ...h,
              isActive: !h.isActive,
              lastModified: new Date().toISOString().split("T")[0],
            };
          }
          return h;
        });
        return updated;
      });

      // Show success message with correct logic
      toast.success(
        `Holiday "${holiday.name}" has been ${holiday.isActive ? "deactivated" : "activated"
        } successfully!`
      );
    } catch (error) {
      // Still update UI even if API call fails
      setHolidays((prevHolidays) =>
        prevHolidays.map((h) =>
          h.id === holidayId
            ? {
              ...h,
              isActive: !h.isActive,
              lastModified: new Date().toISOString().split("T")[0],
            }
            : h
        )
      );
      toast.success(
        `Holiday "${holiday.name}" has been ${holiday.isActive ? "deactivated" : "activated"
        } successfully!`
      );
    }
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = holidays.map(h => h.id || h._id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getHolidaysForDate = (date) => {
    const holidaysForDate = filteredHolidays.filter((holiday) => shouldHolidayAppearOnDate(holiday, date));

    return holidaysForDate;
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      // Normalize to the first day to avoid month rollover (e.g., Jan 31 -> Mar)
      const base = new Date(prev.getFullYear(), prev.getMonth(), 1);
      base.setMonth(base.getMonth() + direction);
      return base;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="h-32 border border-gray-200 bg-gray-50"
        ></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const dayHolidays = getHolidaysForDate(date);
      const isToday = date.toDateString() === today.toDateString();
      const hasHolidays = dayHolidays.length > 0;
      const hasActiveHolidays = dayHolidays.some((h) => h.isActive);
      const hasNationalHolidays = dayHolidays.some(
        (h) => h.type === "National Holiday"
      );
      const hasRegionalHolidays = dayHolidays.some(
        (h) => h.type === "Regional Holiday"
      );
      const hasCompanyHolidays = dayHolidays.some(
        (h) => h.type === "Company Holiday" || h.type === "Company Event"
      );

      // Determine background color based on holiday types
      let dayBgClass = "bg-white";
      let dayBorderClass = "border-gray-200";

      if (isToday) {
        dayBgClass = "bg-brand/5";
        dayBorderClass = "border-brand/30";
      } else if (hasActiveHolidays) {
        if (hasNationalHolidays) {
          dayBgClass = "bg-red-50";
          dayBorderClass = "border-red-300";
        } else if (hasRegionalHolidays) {
          dayBgClass = "bg-orange-50";
          dayBorderClass = "border-orange-300";
        } else if (hasCompanyHolidays) {
          dayBgClass = "bg-brand/5";
          dayBorderClass = "border-brand/30";
        } else {
          dayBgClass = "bg-brand/5";
          dayBorderClass = "border-brand/30";
        }
      }

      days.push(
        <div
          key={day}
          className={`h-32 border ${dayBorderClass} p-2 ${dayBgClass} hover:shadow-md transition-shadow`}
        >
          <div
            className={`text-[13px] font-semibold mb-1 ${isToday
              ? "text-brand"
              : hasActiveHolidays
                ? "text-gray-800"
                : "text-gray-900"
              }`}
          >
            {day}
          </div>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {dayHolidays.slice(0, 3).map((holiday) => {
              // Check if this is a recurring holiday instance
              const isRecurringInstance = holiday.isRecurring &&
                (holiday.recurringType === 'yearly' || holiday.recurringType === 'monthly' || holiday.recurringType === 'weekly');

              return (
                <div
                  key={`${holiday.id}-${date.toISOString().split('T')[0]}`}
                  className={`text-[11px] px-2 py-1 rounded-full truncate cursor-pointer font-medium shadow-sm hover:shadow-md transition-all ${holiday.type === "National Holiday"
                    ? "bg-red-200 text-red-900 border border-red-300"
                    : holiday.type === "Regional Holiday"
                      ? "bg-orange-200 text-orange-900 border border-orange-300"
                      : holiday.type === "Company Holiday"
                        ? "bg-brand/20 text-brand-dark border border-brand/30"
                        : holiday.type === "Company Event"
                          ? "bg-brand/10 text-brand-dark border border-brand/20"
                          : "bg-brand/10 text-brand-dark border border-brand/20"
                    } ${!holiday.isActive ? "opacity-50" : ""} ${isRecurringInstance ? "ring-1 ring-yellow-400" : ""}`}
                  onClick={() => handleViewDetails(holiday)}
                  title={`${holiday.name} - ${holiday.type}${isRecurringInstance ? ` (Recurring ${holiday.recurringType})` : ""
                    }${!holiday.isActive ? " (Inactive)" : ""}`}
                >
                  <div className="flex items-center space-x-1">
                    {isRecurringInstance && (
                      <span className="text-yellow-600">🔄</span>
                    )}
                    {holiday.isOptional && (
                      <span className="text-[8px] bg-yellow-500 text-yellow-900 px-1.5 py-0.5 rounded-sm font-bold mr-1 uppercase">Restricted</span>
                    )}
                    <span className="truncate capitalize">{holiday.name}</span>
                  </div>
                </div>
              );
            })}
            {dayHolidays.length > 3 && (
              <div className="text-[11px] text-gray-600 font-medium bg-gray-200 px-2 py-1 rounded-full">
                +{dayHolidays.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-lg overflow-hidden">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="bg-gray-100 border-b border-gray-200 p-3 text-center text-[12px] font-semibold text-gray-700"
          >
            {day}
          </div>
        ))}
        {days}
      </div>
    );
  };

  if (showDetails && selectedHoliday) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowDetails(false);
                if (onClose) onClose();
              }}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              Holiday Details
            </h2>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Holiday Name:</span>
                  <div className="text-sm font-medium break-words whitespace-normal capitalize">
                    {selectedHoliday.name}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Type:</span>
                  <div className="text-sm font-medium truncate capitalize">
                    {selectedHoliday.type}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Description:</span>
                  <div className="text-sm font-medium break-words whitespace-normal capitalize">
                    {selectedHoliday.description}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Applicable Roles:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedHoliday.applicableRoles && selectedHoliday.applicableRoles.length > 0
                      ? selectedHoliday.applicableRoles.map((role, idx) => (
                          <span
                            key={idx}
                            className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-brand/10 text-brand capitalize"
                          >
                            {role}
                          </span>
                        ))
                      : <span className="text-sm font-medium text-gray-500">All Employees</span>
                    }
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 capitalize ${selectedHoliday.isActive
                      ? "bg-brand/10 text-brand"
                      : "bg-red-100 text-red-800"
                      }`}
                  >
                    {selectedHoliday.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {/* Date Information */}
            <div className="bg-brand/5 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Date Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Start Date:</span>
                  <div className="text-sm font-medium flex items-center space-x-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>
                      {formatDate(selectedHoliday.date, { includeTime: false })}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">End Date:</span>
                  <div className="text-sm font-medium flex items-center space-x-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>
                      {formatDate(selectedHoliday.endDate, { includeTime: false })}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Duration:</span>
                  <div className="text-sm font-medium">
                    {calculateDuration(selectedHoliday.date, selectedHoliday.endDate)} day(s)
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Recurring:</span>
                  <div className="text-sm font-medium">
                    {selectedHoliday.isRecurring && selectedHoliday.recurringType && selectedHoliday.recurringType !== 'none'
                      ? `Yes (${selectedHoliday.recurringType})`
                      : "No"}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Restricted Holiday:</span>
                  <div className={`text-sm font-bold ${selectedHoliday.isOptional ? "text-brand" : "text-gray-400"}`}>
                    {selectedHoliday.isOptional ? "Yes" : "No"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Audit Information */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Audit Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Created Date:</span>
                <div className="text-sm font-medium flex items-center space-x-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>
                    {selectedHoliday.createdDate ? formatDate(selectedHoliday.createdDate, { includeTime: false }) : 'N/A'}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Last Modified:</span>
                <div className="text-sm font-medium flex items-center space-x-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>
                    {selectedHoliday.lastModified ? formatDate(selectedHoliday.lastModified, { includeTime: false }) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showHolidayForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetForm}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingHoliday ? "Edit Holiday" : "New Holiday"}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmitHoliday} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Holiday Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={100}
                  required
                  placeholder="e.g., Diwali"
                  className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                <p className="absolute bottom-[-20px] right-0 text-xs text-gray-500">
                  {formData.name.length}/100 characters
                </p>
              </div>
              {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Holiday Type <span className="text-red-500">*</span>
              </label>
              <select
                name="holidayType"
                value={formData.holidayType || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedType = availableHolidayTypes.find(t => t._id === selectedId);
                  setFormData(prev => ({
                    ...prev,
                    holidayType: selectedId,
                    type: selectedType ? selectedType.name : ""
                  }));
                  if (selectedId) setFormErrors(prev => ({ ...prev, holidayType: "" }));
                }}
                onBlur={handleBlur}
                required
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.holidayType ? 'border-red-500' : 'border-gray-300'}`}
              >
                <option value="">Select holiday type</option>
                {availableHolidayTypes.map((type) => (
                  <option key={type._id} value={type._id} className="capitalize">
                    {type.name}
                  </option>
                ))}
              </select>
              {formErrors.holidayType && <p className="text-xs text-red-500 mt-1">{formErrors.holidayType}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onClick={(e) => e.target.showPicker()}
                required
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.date ? 'border-red-500' : 'border-gray-300'}`}
              />
              {formErrors.date && <p className="text-xs text-red-500 mt-1">{formErrors.date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate || formData.date}
                onChange={handleInputChange}
                min={formData.date}
                onClick={(e) => e.target.showPicker()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              />
              {formData.date && (
                <div className="mt-2 text-sm text-gray-600">
                  <span className="font-medium">Duration: </span>
                  <span className="text-brand">
                    {calculateDuration(formData.date, formData.endDate || formData.date)} day(s)
                  </span>
                  {formData.date === (formData.endDate || formData.date) && (
                    <span className="ml-2 text-xs text-gray-500">(Single day holiday)</span>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <select
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="All Locations">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span> <span className="text-gray-500 text-xs font-normal">(10-500 characters)</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}

              required
              rows={3}
              maxLength={1500}
              onBlur={handleBlur}
              placeholder="Describe the holiday..."
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
            />
            <div className="flex justify-between items-center mt-1">
              {formErrors.description ? (
                <p className="text-sm text-red-600">{formErrors.description}</p>
              ) : (
                <div></div>
              )}
              <span className="text-xs text-gray-500">
                {formData.description.length}/1500
              </span>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applicable Roles <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              {roles.map((role) => (
                <label key={role} className="flex items-center space-x-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.applicableRoles.includes(role)}
                    onChange={() => handleRoleChange(role)}
                    className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded transition-colors group-hover:border-brand"
                  />
                  <span className="text-sm text-gray-700 capitalize group-hover:text-brand transition-colors">{role}</span>
                </label>
              ))}
            </div>
            {formData.applicableRoles.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please select at least one role. Default is "All Employees".</p>
            )}
          </div>

          {/* Recurring Settings */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                name="isRecurring"
                checked={formData.isRecurring}
                onChange={handleInputChange}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Recurring Holiday
              </label>
            </div>
            {formData.isRecurring && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recurrence Type
                </label>
                <select
                  name="recurringType"
                  value={formData.recurringType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  {recurringTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {formData.isRecurring && formData.date && (
                  <div className="mt-3 p-3 bg-brand/5 rounded-lg border border-brand/20">
                    <div className="flex items-center space-x-2">
                      <span className="text-brand">🔄</span>
                      <span className="text-sm font-medium text-brand">
                        This holiday will repeat {formData.recurringType === 'yearly' ? 'every year' :
                          formData.recurringType === 'monthly' ? 'every month' :
                            formData.recurringType === 'weekly' ? 'every week' : 'as specified'} on the same date(s)
                      </span>
                    </div>
                    {formData.recurringType === 'yearly' && (
                      <div className="mt-2 text-xs text-brand">
                        Example: If you set Jan 1, 2024, it will appear on Jan 1, 2025, Jan 1, 2026, etc.
                      </div>
                    )}
                    {formData.recurringType === 'monthly' && (
                      <div className="mt-2 text-xs text-brand">
                        Example: If you set the 15th, it will appear on the 15th of every month.
                      </div>
                    )}
                    {formData.recurringType === 'weekly' && (
                      <div className="mt-2 text-xs text-brand">
                        Example: If you set a Monday, it will appear every Monday.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Options */}
          <div className="mt-6 space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="isOptional"
                checked={formData.isOptional}
                onChange={handleInputChange}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Restricted Holiday
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Active Holiday
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={resetForm} aria-label="Cancel holiday">
              Cancel holiday
            </Button>
            <PermissionGate permission="attendance" action={editingHoliday ? "update" : "create"}>
              <Button
                type="submit"
                variant="primary"
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
                aria-label={editingHoliday ? "Update holiday" : "Create holiday"}
              >
                <Save className="h-4 w-4" />
                <span>
                  {editingHoliday ? "Update holiday" : "Create holiday"}
                </span>
              </Button>
            </PermissionGate>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Holiday Calendar
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {viewMode === "list" && (
              <PermissionGate permission="attendance" action="read">
                <div className="relative" ref={selectedExportWrapRef}>
                  <Button
                    variant="outline"
                    icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
                    onClick={handleExportClick}
                    disabled={exportLoading}
                    className={selectedRows.size > 0 ? "bg-brand/5 border-brand/20 text-brand" : ""}
                  >
                    {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
                  </Button>
                  <SelectedExportFormatBar
                    show={selectedRows.size > 0 && showInlineSelectedFormats}
                    panelEntered={inlineFormatPanelEntered}
                    disabled={exportLoading}
                    onSelect={handleSelectedFormatExport}
                  />
                </div>
                {selectedRows.size > 0 && (
                  <PermissionGate permission="attendance" action="delete">
                    <Button
                      variant="outline"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={handleBulkDelete}
                      className="bg-red-50 border-red-200 text-red-600 hover:bg-red-50 focus:ring-2 focus:ring-red-500 focus:outline-none focus:border-transparent"
                    >
                      Delete Selected ({selectedRows.size})
                    </Button>
                  </PermissionGate>
                )}
              </PermissionGate>

            )}
            <div className="flex items-center space-x-2 bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode("calendar")}
                className={`p-2 rounded-lg flex items-center justify-center transition-all ${
                  viewMode === "calendar"
                    ? "bg-[#0F2942] text-white shadow-sm"
                    : "text-slate-500 hover:text-[#0F2942] hover:bg-slate-200"
                }`}
              >
                <Calendar className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg flex items-center justify-center transition-all ${
                  viewMode === "list"
                    ? "bg-[#0F2942] text-white shadow-sm"
                    : "text-slate-500 hover:text-[#0F2942] hover:bg-slate-200"
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <PermissionGate permission="attendance" action="create">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkUploadModal(true)}
                  className="flex items-center space-x-2 border-brand text-brand hover:bg-brand/5"
                >
                  <Plus className="h-4 w-4" />
                  <span>Bulk Upload</span>
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowHolidayForm(true)}
                  className="flex items-center space-x-2 !bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white border-transparent"
                >
                  <Plus className="h-4 w-4" />
                  <span>New Holiday</span>
                </Button>
              </div>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search holidays..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              maxLength={100}
              className="w-full h-11 pl-10 pr-10 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
          </div>

          {/* Date Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              allowFutureDates={true}
              onChange={setDateRange}
            />
          </div>

          {/* Type Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 h-11 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand capitalize"
            >
              <option value="all">All Types</option>
              {availableHolidayTypes.map((type) => (
                <option key={type._id || type.id} value={type._id || type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {(searchTerm ||
            filterType !== "all" ||
            filterStartDate ||
            filterEndDate) && (
              <div className="flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    resetHolidayFilters();
                  }}
                  className="flex items-center space-x-2 text-gray-600 hover:text-brand border-gray-300 hover:border-brand px-4 h-11"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>Clear</span>
                </Button>
              </div>
            )}
        </div>
      </div>

      {viewMode === "calendar" ? (
        <>
          {/* Calendar Navigation */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMonth(-1)}
                className="flex items-center space-x-2"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </Button>
              <h3 className="text-lg font-semibold text-gray-900">
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMonth(1)}
                className="flex items-center space-x-2"
                aria-label="Next month"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-6">{renderCalendar()}</div>
        </>
      ) : (
        /* List View */
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
              <tr>
                <th className="px-6 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-brand focus:ring-brand h-3 w-3"
                    checked={holidays.length > 0 && selectedRows.size === holidays.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider w-1/3 cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Holiday Name</span>
                    {sortField === 'name' ? (
                      sortOrder === 'asc' ?
                        <ArrowUp className="h-3 w-3 text-brand" /> :
                        <ArrowDown className="h-3 w-3 text-brand" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Date</span>
                    {sortField === 'date' ? (
                      sortOrder === 'asc' ?
                        <ArrowUp className="h-3 w-3 text-brand" /> :
                        <ArrowDown className="h-3 w-3 text-brand" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {sortField === 'type' ? (
                      sortOrder === 'asc' ?
                        <ArrowUp className="h-3 w-3 text-brand" /> :
                        <ArrowDown className="h-3 w-3 text-brand" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider">
                  Restricted
                </th>
                <th
                  className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('createdDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created Date</span>
                    {sortField === 'createdDate' ? (
                      sortOrder === 'asc' ?
                        <ArrowUp className="h-3 w-3 text-brand" /> :
                        <ArrowDown className="h-3 w-3 text-brand" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortOrder === 'asc' ?
                        <ArrowUp className="h-3 w-3 text-brand" /> :
                        <ArrowDown className="h-3 w-3 text-brand" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                {userRole !== "employee" && (
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading || isSearching ? (
                <tr>
                  <td colSpan={userRole !== "employee" ? 6 : 5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mb-2"></div>
                      <p className="text-sm text-gray-500">
                        {isSearching ? "Searching holidays..." : "Loading holidays..."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : holidays.length === 0 ? (
                <tr>
                  <td colSpan={userRole !== "employee" ? 7 : 6} className="px-6 py-12 text-center">
                    <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No holidays found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your search criteria or create a new holiday.
                    </p>
                  </td>
                </tr>
              ) : (
                getSortedHolidays(holidays).map((holiday) => (
                  <tr key={holiday.id} className={`hover:bg-blue-50/30 ${selectedRows.has(holiday.id || holiday._id) ? 'bg-brand/5' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand focus:ring-brand h-3 w-3"
                        checked={selectedRows.has(holiday.id || holiday._id)}
                        onChange={() => handleSelectRow(holiday.id || holiday._id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap overflow-hidden">
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium text-gray-900 flex items-center space-x-2">
                          {holiday.isRecurring && (
                            <span className="text-yellow-600 flex-shrink-0" title={`Recurring ${holiday.recurringType}`}>
                              🔄
                            </span>
                          )}
                          <span className="truncate max-w-xs capitalize">{holiday.name}</span>
                        </div>
                        <div className="text-[12px] text-gray-500 truncate max-w-xs capitalize">
                          {holiday.description}
                          {holiday.isRecurring && (
                            <span className="ml-2 text-yellow-600 font-medium">
                              (Recurring {holiday.recurringType})
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[12px] text-gray-900">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span>{formatDate(holiday.date, { includeTime: false })}</span>
                        {holiday.date !== holiday.endDate && (
                          <span>
                            {" "}
                            - {formatDate(holiday.endDate, { includeTime: false })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap overflow-hidden">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full truncate capitalize ${holiday.type === "National Holiday"
                          ? "bg-red-100 text-red-800"
                          : holiday.type === "Regional Holiday"
                            ? "bg-orange-100 text-orange-800"
                            : holiday.type === "Company Holiday"
                              ? "bg-brand/10 text-brand-dark"
                              : "bg-brand/10 text-brand-dark"
                          }`}
                      >
                        {holiday.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {holiday.isOptional ? (
                        <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-brand/10 text-brand border border-brand/20">
                          YES
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[12px] text-gray-900">
                      {holiday.createdAt ? formatDate(holiday.createdAt, { includeTime: true }) : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${holiday.isActive
                            ? "bg-brand/10 text-brand"
                            : "bg-red-100 text-red-800"
                            }`}
                        >
                          {holiday.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                    {userRole !== "employee" && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <PermissionGate permission="attendance" action="read">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(holiday)}
                              className="text-brand hover:text-brand-dark"
                              title="View Details"
                              aria-label="View holiday details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="update">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditHoliday(holiday)}
                              className="text-brand hover:text-brand-dark"
                              title="Edit Holiday"
                              aria-label="Edit holiday"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="delete">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteHoliday(holiday.id, e)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Holiday"
                              aria-label="Delete holiday"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
            {/* Pagination - Only show for list view */}
            {(() => {
              const holidayTotalItems = Number(pagination?.totalItems || holidays.length || 0);
              const holidayTotalPages = Number(pagination?.totalPages || 1);
              const effectivePaginationLimit = pagination?.limit || pagination?.itemsPerPage || itemsPerPage || 10;
              const currentPageNum = pagination?.currentPage || currentPage || 1;
              const shouldShowPagination = !loading && !isSearching;
              const handleHolidayPageChange = (page) => {
                handlePageChange(page);
              };

              // Calculate hasPrevPage and hasNextPage if not provided
              const hasPrevPage = pagination?.hasPrevPage !== undefined
                ? pagination.hasPrevPage
                : currentPageNum > 1;
              const hasNextPage = pagination?.hasNextPage !== undefined
                ? pagination.hasNextPage
                : currentPageNum < holidayTotalPages;

              return shouldShowPagination ? (
                  <PaginationControls
                    currentPage={currentPageNum}
                    totalPages={holidayTotalPages}
                    totalItems={holidayTotalItems}
                    limit={effectivePaginationLimit}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                    onPageChange={handleHolidayPageChange}
                    onLimitChange={handleItemsPerPageChange}
                    limitOptions={[10, 25, 50, 100]}
                    border={false}
                  />
              ) : null;
            })()}
          </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && holidayToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900 break-words whitespace-normal">
                Delete Holiday
              </h3>
            </div>

            <p className="text-gray-600 mb-6 break-words whitespace-normal">
              Are you sure you want to delete "{holidayToDelete.name || "Holiday"}"? This
              action cannot be undone.
            </p>

            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={cancelDeleteHoliday}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-blue-50/30 transition-colors break-words whitespace-normal"
              >
                Cancel
              </button>
              <PermissionGate permission="attendance" action="delete">
                <button
                  onClick={confirmDeleteHoliday}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center break-words whitespace-normal"
                >
                  <Trash2 className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>Delete</span>
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900 break-words whitespace-normal">
                Bulk Delete Holidays
              </h3>
            </div>

            <p className="text-gray-600 mb-6 break-words whitespace-normal">
              Are you sure you want to delete {selectedRows.size} selected holidays? This
              action cannot be undone.
            </p>

            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={cancelDeleteHoliday}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-blue-50/30 transition-colors break-words whitespace-normal"
              >
                Cancel
              </button>
              <PermissionGate permission="attendance" action="delete">
                <button
                  onClick={confirmBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center break-words whitespace-normal"
                >
                  <Trash2 className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>Delete Selected</span>
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Holiday Data"
        showDateRange
        scopedFormatExport={{
          onExport: handleScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            loadHolidays();
          }}
          config={{
            title: 'Bulk Upload Holidays',
            subtitle: 'Import multiple holidays using an Excel file',
            templateEndpoint: '/holidays/bulk-upload/template',
            validateEndpoint: '/holidays/bulk-upload/validate',
            uploadEndpoint: '/holidays/bulk-upload',
            templateFilename: 'holiday_template.xlsx',
            dataKey: 'holidays',
            instructions: [
              'Fill in holiday details in the Excel template',
              'Required fields: name, date, holidayType, description, location, applicableRoles',
              'Optional fields: isOptional (true/false), isRecurring (true/false), recurringType, endDate, isActive',
              'Date format: DD-MM-YYYY (e.g., 01-01-2024)',
              `Holiday Type: ${availableHolidayTypes.map(t => t.name).join(', ') || 'National Holiday, Regional Holiday, Company Holiday'}`,
              `Recurrence Type: ${recurringTypes.map(t => t.value).join(', ')}`,
              `Applicable Roles: ${roles.join(', ')}`
            ]
          }}
        />
      )}
    </div>
  );
};

export default HolidayCalendar;

